<template>
  <HeaderBlue
    :ante-title="content.eyebrow"
    :title="content.h1"
    :post-title="content.heroIntro"
    :background-image="heroImage"
  />

  <OrderLesson ref="orderLesson" :lesson="content.lesson" />

  <main class="elearning-page">
    <section id="offre" class="offer-section">
      <div class="container offer-section__inner">
        <div class="offer-section__content">
          <p class="section-label">Théorie, pratique et examen inclus</p>
          <h2>{{ content.offerTitle }}</h2>
          <p v-html="content.offerIntro"></p>
          <p v-html="content.offerDetail"></p>

          <ul class="offer-section__benefits">
            <li v-for="benefit in content.offerBenefits" :key="benefit">
              {{ benefit }}
            </li>
          </ul>
        </div>

        <aside
          class="offer-price"
          :aria-label="`Tarif de la formation ${content.type} en ligne`"
        >
          <p class="offer-price__label">La formule complète</p>
          <p class="offer-price__value">549 <span>€</span></p>
          <div class="offer-price__separator"></div>
          <p class="offer-price__payment">
            Paiement possible en<br />
            <strong>4 × 137,25 € sans frais</strong>
          </p>
          <p class="offer-price__included">
            E-learning + pratique avec véhicule + frais d'examen
          </p>
          <a class="site-button site-button--yellow" href="#inscription">
            Choisir cette formation
          </a>
        </aside>
      </div>
    </section>

    <section class="learning-sections" aria-label="Programme de la formation">
      <article
        v-for="(feature, index) in content.firstFeatures"
        :key="feature.title"
        class="learning-section"
        :class="{
          'learning-section--reverse': index % 2 === 1,
          'learning-section--tinted': index % 2 === 1,
        }"
      >
        <div class="container learning-section__inner">
          <div class="learning-section__content">
            <p class="section-label">Étape {{ index + 1 }}</p>
            <h2>{{ feature.title }}</h2>
            <p v-html="feature.copy"></p>
            <p v-html="feature.detail"></p>
            <ul>
              <li v-for="item in feature.bullets" :key="item">{{ item }}</li>
            </ul>
          </div>

          <figure class="learning-section__media">
            <img
              :src="getFeatureImage(feature.image)"
              :alt="feature.alt"
              width="900"
              height="650"
              loading="lazy"
            />
          </figure>
        </div>
      </article>
    </section>

    <section id="inscription" class="elearning-cta">
      <div class="container elearning-cta__inner">
        <div class="elearning-cta__content">
          <p class="section-label section-label--light">Passez à l'action</p>
          <h2>{{ content.ctaTitle }}</h2>
          <p>{{ content.ctaCopy }}</p>
        </div>
        <button
          type="button"
          class="site-button site-button--yellow elearning-cta__button"
          @click="openOrderLesson"
        >
          S'inscrire à la prochaine session
        </button>
      </div>
    </section>

    <section class="learning-sections" aria-label="Entraînement et suivi">
      <article
        v-for="(feature, index) in content.secondFeatures"
        :key="feature.title"
        class="learning-section"
        :class="{
          'learning-section--reverse': index % 2 === 1,
          'learning-section--tinted': index % 2 === 1,
        }"
      >
        <div class="container learning-section__inner">
          <div class="learning-section__content">
            <p class="section-label">Étape {{ index + 3 }}</p>
            <h2>{{ feature.title }}</h2>
            <p v-html="feature.copy"></p>
            <p v-html="feature.detail"></p>
            <ul>
              <li v-for="item in feature.bullets" :key="item">{{ item }}</li>
            </ul>
          </div>

          <figure class="learning-section__media">
            <img
              :src="getFeatureImage(feature.image)"
              :alt="feature.alt"
              width="900"
              height="650"
              loading="lazy"
            />
          </figure>
        </div>
      </article>
    </section>

    <nav class="elearning-page__back" aria-label="Retour aux formations">
      <router-link :to="`/formations-taxi-vtc/${content.backSection}`">
        <span aria-hidden="true">&#171;</span>
        Voir toutes les formations {{ content.type }}
      </router-link>
    </nav>
  </main>
</template>

<script>
import HeaderBlue from "@/components/headers/HeaderBlue.vue";
import OrderLesson from "@/components/plugins/OrderLesson.vue";

const featureImages = {
  "vtc-elearning1.webp": require("@/assets/pictures/vtc-elearning1.webp"),
  "vtc-elearning2.webp": require("@/assets/pictures/vtc-elearning2.webp"),
  "vtc-elearning3.webp": require("@/assets/pictures/vtc-elearning3.webp"),
  "vtc-elearning4.webp": require("@/assets/pictures/vtc-elearning4.webp"),
};

export default {
  name: "ElearningFormationPage",
  components: { HeaderBlue, OrderLesson },
  props: {
    content: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      heroImage: require("@/assets/pictures/vtc-elearning.webp"),
    };
  },
  methods: {
    getFeatureImage(imageName) {
      return featureImages[imageName];
    },
    openOrderLesson() {
      this.$refs.orderLesson.showCallForm();
    },
  },
};
</script>

<style lang="scss" scoped>
.elearning-page {
  background: $background;
  overflow: hidden;
}

.section-label {
  margin: 0 0 14px;
  color: $color-primary;
  font-family: "adam";
  font-size: 0.78rem;
  letter-spacing: 0.18em;
  line-height: 1.4;
  text-transform: uppercase;

  &::before {
    content: "";
    display: inline-block;
    width: 42px;
    height: 4px;
    margin: 0 12px 2px 0;
    background: $color-secondary;
  }
}

.section-label--light {
  color: rgba(255, 255, 255, 0.82);
}

.offer-section {
  padding: clamp(62px, 8vw, 108px) 0;
  background: #ffffff;
  border-bottom: 8px solid $color-secondary;
}

.offer-section__inner {
  display: grid;
  gap: clamp(34px, 6vw, 78px);
  align-items: stretch;

  @media screen and (min-width: 992px) {
    grid-template-columns: minmax(0, 1.45fr) minmax(330px, 0.55fr);
  }
}

.offer-section__content {
  align-self: center;

  h2 {
    max-width: 800px;
    margin: 0 0 26px;
    color: $color-primary;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    font-size: clamp(1.8rem, 3vw, 2.75rem);
    line-height: 1.12;
    text-wrap: balance;
  }

  > p:not(.section-label) {
    max-width: 820px;
    margin: 0 0 17px;
    color: $color-text;
    font-size: clamp(1rem, 1.2vw, 1.12rem);
    line-height: 1.78;
  }

  :deep(strong) {
    color: $color-primary;
    font-weight: 700 !important;
  }
}

.offer-section__benefits {
  display: grid;
  gap: 12px 26px;
  margin: 30px 0 0;
  padding: 25px 0 0;
  border-top: 2px solid rgba(0, 42, 84, 0.12);
  list-style: none;

  @media screen and (min-width: 680px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  li {
    position: relative;
    padding-left: 27px;
    color: $color-text;
    font-size: 1rem;
    line-height: 1.5;

    &::before {
      content: "";
      position: absolute;
      top: 0.45em;
      left: 0;
      width: 11px;
      height: 11px;
      background: $color-secondary;
      box-shadow: 5px 5px 0 $color-primary;
    }
  }
}

.offer-price {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: clamp(30px, 4vw, 48px);
  border-left: 9px solid $color-secondary;
  background: $color-primary;
  color: #ffffff;
}

.offer-price__label {
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-family: "adam";
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.offer-price__value {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 12px 0 0;
  color: $color-secondary;
  font-family: "Spartan", "Arial Black", Arial, sans-serif;
  font-weight: 700;
  font-size: clamp(3.7rem, 7vw, 5.6rem);
  line-height: 0.95;
  white-space: nowrap;

  span {
    font-size: 0.48em;
  }
}

.offer-price__separator {
  width: 100%;
  height: 1px;
  margin: 24px 0 20px;
  background: rgba(255, 255, 255, 0.22);
}

.offer-price__payment {
  margin: 0 0 14px;
  color: #ffffff;
  font-size: 1rem;
  line-height: 1.55;

  strong {
    color: $color-secondary;
    font-weight: 700 !important;
  }
}

.offer-price__included {
  margin: 0 0 26px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.88rem;
  line-height: 1.55;
}

.site-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 12px 20px;
  border: 2px solid $color-secondary;
  color: $color-primary;
  font-family: "adam";
  font-size: 0.76rem;
  letter-spacing: 0.06em;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #ffffff;
    color: $color-primary;
    text-decoration: none;
  }
}

.site-button--yellow {
  background: $color-secondary;
}

.learning-section {
  padding: clamp(66px, 8vw, 112px) 0;
  background: #ffffff;

  &--tinted {
    background: #edf3f7;
  }
}

.learning-section__inner {
  display: grid;
  gap: clamp(38px, 6vw, 86px);
  align-items: center;

  @media screen and (min-width: 992px) {
    grid-template-columns: minmax(0, 1fr) minmax(400px, 0.82fr);
  }
}

.learning-section--reverse {
  @media screen and (min-width: 992px) {
    .learning-section__inner {
      grid-template-columns: minmax(400px, 0.82fr) minmax(0, 1fr);
    }

    .learning-section__media {
      grid-column: 1;
      grid-row: 1;
    }

    .learning-section__content {
      grid-column: 2;
      grid-row: 1;
    }
  }
}

.learning-section__content {
  h2 {
    margin: 0 0 24px;
    color: $color-primary;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    font-size: clamp(1.65rem, 2.6vw, 2.45rem);
    line-height: 1.14;
    text-wrap: balance;
  }

  > p:not(.section-label) {
    margin: 0 0 16px;
    color: $color-text;
    font-size: clamp(1rem, 1.15vw, 1.1rem);
    line-height: 1.78;
  }

  :deep(strong) {
    color: $color-primary;
    font-weight: 700 !important;
  }

  ul {
    margin: 28px 0 0;
    padding: 22px 0 0;
    border-top: 1px solid rgba(0, 42, 84, 0.18);
    list-style: none;
  }

  li {
    position: relative;
    margin: 0 0 10px;
    padding-left: 25px;
    color: $color-primary;
    font-size: 0.98rem;
    line-height: 1.5;

    &::before {
      content: "→";
      position: absolute;
      left: 0;
      color: $color-secondary;
      font-weight: 800;
    }
  }
}

.learning-section__media {
  position: relative;
  margin: 0;

  &::before {
    content: "";
    position: absolute;
    right: -14px;
    bottom: -14px;
    width: 46%;
    height: 46%;
    background: $color-secondary;
  }

  img {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    aspect-ratio: 4 / 3;
    border: 4px solid $color-primary;
    object-fit: cover;
    box-shadow: 0 18px 32px rgba(0, 42, 84, 0.14);
  }
}

.elearning-cta {
  scroll-margin-top: 100px;
  border-top: 9px solid $color-secondary;
  border-bottom: 9px solid $color-secondary;
  background: $color-primary;
}

.elearning-cta__inner {
  display: grid;
  gap: 34px;
  align-items: center;
  padding-top: clamp(48px, 6vw, 72px);
  padding-bottom: clamp(48px, 6vw, 72px);

  @media screen and (min-width: 850px) {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .elearning-cta__button {
    border: 2px solid $color-secondary;
    border-radius: 0;
    background: $color-secondary;
    color: $color-primary;
    box-shadow: none;
  }
}

.elearning-cta__content {
  h2 {
    margin: 0 0 15px;
    color: #ffffff;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    font-size: clamp(1.65rem, 3vw, 2.65rem);
    line-height: 1.12;
    text-wrap: balance;
  }

  > p:last-child {
    margin: 0;
    color: rgba(255, 255, 255, 0.84);
    font-size: clamp(1rem, 1.2vw, 1.12rem);
    line-height: 1.7;
  }
}

.elearning-page__back {
  display: flex;
  justify-content: center;
  padding: 42px 20px 58px;
  background: #ffffff;

  a {
    color: $color-primary;
    font-family: "adam";
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-decoration: none;
    text-transform: uppercase;

    &:hover {
      color: $color-primary;
      text-decoration: underline;
    }
  }

  span {
    margin-right: 8px;
    color: $color-secondary;
    font-size: 1rem;
  }
}

@media screen and (max-width: 991px) {
  .learning-section__content {
    order: 1;
  }

  .learning-section__media {
    order: 2;
    margin-right: 14px;
  }
}

@media screen and (max-width: 575px) {
  .offer-section__inner,
  .learning-section__inner,
  .elearning-cta__inner {
    padding-right: 22px;
    padding-left: 22px;
  }

  .offer-price {
    padding: 30px 26px;
  }

  .site-button {
    width: 100%;
  }

  .learning-section__media::before {
    right: -10px;
    bottom: -10px;
  }
}
</style>
